import React from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { UnlockIcon } from '../../common/components';
import { managementService } from '../service/management.service';
import { Ikey } from '../types.type';

interface IProps {
  row: Ikey;
  className?: string;
}

export const ReleaseButton = ({ row, className = '' }: IProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { backend, springId, activations, usedActivations } = row;

  // The column means the same thing on both servers - "free every machine on
  // this licence" - and the two say it differently. LicenseSpring has one call
  // that does it; ours releases a seat at a time, because ours also offers
  // per-machine release below the table and one endpoint serves both.
  const releaseMutation = useMutation({
    mutationKey: ['releaseActivations', backend ?? 'spring', springId || row.key],
    mutationFn: async () => {
      if (backend !== 'squarebit') {
        return managementService.releaseAllActivations(springId);
      }
      // Sequential, not Promise.all: each release decrements the same seat
      // count server-side, and firing them together is how you get a race
      // where the last response reports a count that was already stale.
      for (const activation of activations ?? []) {
        await managementService.releaseActivation(activation.id);
      }
      return undefined;
    },
    onSuccess: () => {
      toast.success(t('management.release.success'));
      queryClient.invalidateQueries({ queryKey: ['keys'] });
      // The Eye section's own query key, so its machine list below the
      // table reflects the release without a reload.
      queryClient.invalidateQueries({ queryKey: ['eye-license'] });
    },
    onError: () => toast.error(t('management.release.error'))
  });

  if (usedActivations === 0) {
    return (
      <span
        title={t('management.release.disabledTitle')}
        className={`inline-flex items-center gap-[4px] px-[8px] h-[26px] rounded-full bg-surface-secondary border border-border text-content-muted font-mono text-[10px] font-medium cursor-not-allowed select-none ${className}`}
      >
        <UnlockIcon />
        {t('management.release.label')}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={t('management.release.activeTitle')}
      disabled={releaseMutation.isPending}
      onClick={() => {
        if (window.confirm(t('management.release.confirm'))) {
          releaseMutation.mutate();
        }
      }}
      className={`inline-flex items-center gap-[4px] px-[8px] h-[26px] rounded-full bg-badge-red-bg/60 border border-badge-red-text/30 text-badge-red-text font-mono text-[10px] font-medium transition-all hover:bg-badge-red-bg hover:border-badge-red-text/50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      <UnlockIcon />
      {t('management.release.label')}
    </button>
  );
};
